import React, { useEffect, useId, useRef } from 'react';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import IconButton from 'components/partials/button/icon-button';

import Icons from 'assets/icons';

interface GuidedPromptHeaderProps {
  questionId: string;
  promptText: string;
  isMultiStep: boolean;
  currentStep: number;
  totalSteps: number;
  isMultiSelect: boolean;
  isRequired: boolean;
  onDismiss?: () => void;
}

const GuidedPromptHeader = ({
  questionId,
  promptText,
  isMultiStep,
  currentStep,
  totalSteps,
  isMultiSelect,
  isRequired,
  onDismiss,
}: GuidedPromptHeaderProps): React.JSX.Element => {
  const subtextId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [currentStep]);

  const headingAccessibleName = isMultiStep
    ? `Question ${currentStep + 1} of ${totalSteps}: ${promptText}`
    : promptText;

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.75,
        pb: 1.25,
        borderBottomWidth: '1px',
        borderColor: 'tokens.gray.100',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 0.5 }}>
        <h4
          ref={headingRef}
          id={questionId}
          tabIndex={-1}
          className="flex-1 text-base leading-5 font-semibold text-navy-800 focus-visible:outline-none"
          aria-label={headingAccessibleName}
          aria-describedby={subtextId}
        >
          {promptText}
        </h4>

        {isMultiStep && (
          <span className="text-base leading-6 text-gray-500 whitespace-nowrap">
            {currentStep + 1} of {totalSteps}
          </span>
        )}

        <IconButton
          variant="neutral-text"
          size="xs"
          aria-label="Close prompt"
          className={isMultiStep ? 'ml-6' : undefined}
          onClick={() => onDismiss?.()}
          icon={<Icons.Close />}
        />
      </Stack>

      <p
        id={subtextId}
        className="text-sm text-gray-500 leading-4 flex items-center gap-1"
      >
        <span>{isMultiSelect ? 'Select all that apply' : 'Select one'}</span>
        {isRequired && <span>(required)</span>}
      </p>
    </Box>
  );
};

export default GuidedPromptHeader;
